/** Tarot draws: a stable "card of the day" and random spreads with optional reversals. */

/** FNV-1a 32-bit hash: tiny, fast, deterministic. */
export function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface Draw {
  index: number;
  reversed: boolean;
}

/** The same witch gets the same card all day; a new card tomorrow. `day` is YYYY-MM-DD. */
export function cardOfTheDay(
  seed: string,
  day: string,
  deckSize: number,
  allowReversals = true,
): Draw {
  const h = hash32(`${seed}:${day}`);
  return { index: h % deckSize, reversed: allowReversals && (h >>> 16) % 4 === 0 };
}

/** Draw `count` distinct cards using a partial Fisher-Yates shuffle. `rng` returns 0..1. */
export function drawCards(
  deckSize: number,
  count: number,
  rng: () => number = Math.random,
  allowReversals = true,
): Draw[] {
  const n = Math.min(count, deckSize);
  const deck = Array.from({ length: deckSize }, (_, i) => i);
  const out: Draw[] = [];
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (deckSize - i));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
    out.push({ index: deck[i]!, reversed: allowReversals && rng() < 0.25 });
  }
  return out;
}

export interface Spread {
  slug: string;
  name: string;
  positions: string[];
}

export const SPREADS: Spread[] = [
  { slug: "single", name: "One Card", positions: ["Guidance"] },
  { slug: "three", name: "Past, Present, Future", positions: ["Past", "Present", "Future"] },
  { slug: "mind-body-spirit", name: "Mind, Body, Spirit", positions: ["Mind", "Body", "Spirit"] },
  {
    slug: "new-moon",
    name: "New Moon Intentions",
    positions: ["What to plant", "What to nurture", "What blocks you", "Hidden help"],
  },
  {
    slug: "full-moon",
    name: "Full Moon Release",
    positions: ["What has bloomed", "What to release", "What to celebrate", "Next step"],
  },
  {
    slug: "celtic-cross",
    name: "Celtic Cross",
    positions: [
      "Present",
      "Challenge",
      "Foundation",
      "Recent past",
      "Possible outcome",
      "Near future",
      "Self",
      "Environment",
      "Hopes and fears",
      "Outcome",
    ],
  },
];
